/* 手書きメッセージの逐次表示(2026-08-17 / 同日「段落ごとのフェードイン」へ変更)

   仕組み:
   1. 手紙画像を解析し、文字行を検出 → 行間より大きい余白で段落にまとめる
   2. 段落ごとにcanvasへ切り出して縦に積む(見た目は元の画像と同一)
   3. セクションが画面に入ったら、上の段落から順にふわっとフェードインする

   配慮:
   - prefers-reduced-motion では何もしない(画像がそのまま見える)
   - JS無効・画像のCORS失敗・解析失敗でも、元の<img>が残るので壊れない
   - 画像はテーマエディタで差し替え可能なため、行検出は実行時に行う */

if (!customElements.get('ny-soda-letter-reveal')) {
  class NySodaLetterReveal extends HTMLElement {
    connectedCallback() {
      this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
      if (this.reduce.matches) return;

      this.img = this.querySelector('.ny-soda-letter__image');
      if (!this.img) return;

      const source = new Image();
      source.crossOrigin = 'anonymous';
      source.src = this.img.currentSrc || this.img.src;
      source
        .decode()
        .then(() => this.build(source))
        .catch(() => {
          /* 解析できない環境では元の画像のまま */
        });
    }

    /* 文字行の検出: 各行の「暗い画素数」を数え、連続する暗い帯を行とみなす。
       帯と帯の間(余白)は隣接する行に半分ずつ含め、画像全体を隙間なく覆う */
    detectBands(source) {
      const W = 220; // 解析は縮小して行う(行の位置が分かれば十分)
      const H = Math.round((source.naturalHeight / source.naturalWidth) * W);
      const canvas = document.createElement('canvas');
      canvas.width = W;
      canvas.height = H;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(source, 0, 0, W, H);
      const data = ctx.getImageData(0, 0, W, H).data;

      const darkness = [];
      for (let y = 0; y < H; y += 1) {
        let n = 0;
        for (let x = 0; x < W; x += 1) {
          const i = (y * W + x) * 4;
          const lum = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
          if (lum < 150) n += 1;
        }
        darkness.push(n);
      }

      // 行の切れ目: 暗い画素がほぼ無い行が3行以上続く区間
      const hasInk = darkness.map((n) => n >= 2);
      const lines = [];
      let start = -1;
      for (let y = 0; y < H; y += 1) {
        if (hasInk[y] && start === -1) start = y;
        if (!hasInk[y] && start !== -1) {
          const gap = hasInk.slice(y, y + 3).some(Boolean);
          if (!gap) {
            lines.push([start, y]);
            start = -1;
          }
        }
      }
      if (start !== -1) lines.push([start, H]);
      if (lines.length < 3) return null; // 検出が怪しければ諦める

      // 段落の切れ目の判定: 余白は「行間(小)」と「段落間(大)」の2グループに
      // 分かれるので、1次元の2クラスタ分割で境界を求める(解析縮尺に依存しない)
      const gaps = [];
      for (let i = 0; i < lines.length - 1; i += 1) {
        gaps.push(lines[i + 1][0] - lines[i][1]);
      }
      let threshold = gaps.reduce((a, b) => a + b, 0) / gaps.length;
      for (let iter = 0; iter < 12; iter += 1) {
        const small = gaps.filter((g) => g <= threshold);
        const large = gaps.filter((g) => g > threshold);
        if (!small.length || !large.length) break;
        const m1 = small.reduce((a, b) => a + b, 0) / small.length;
        const m2 = large.reduce((a, b) => a + b, 0) / large.length;
        const next = (m1 + m2) / 2;
        if (Math.abs(next - threshold) < 0.01) break;
        threshold = next;
      }
      // 2グループの差が乏しい(全部ほぼ同じ余白)なら段落分割はしない
      const maxGap = Math.max(...gaps);
      const minGap = Math.min(...gaps);
      if (maxGap < minGap * 1.5) threshold = Infinity;
      const paragraphs = [[...lines[0]]];
      for (let i = 1; i < lines.length; i += 1) {
        if (gaps[i - 1] > threshold) {
          paragraphs.push([...lines[i]]);
        } else {
          paragraphs[paragraphs.length - 1][1] = lines[i][1];
        }
      }

      // 余白を隣接段落へ配分して連続した帯にする
      const bands = [];
      for (let i = 0; i < paragraphs.length; i += 1) {
        const top = i === 0 ? 0 : (paragraphs[i - 1][1] + paragraphs[i][0]) / 2;
        const bottom =
          i === paragraphs.length - 1 ? H : (paragraphs[i][1] + paragraphs[i + 1][0]) / 2;
        bands.push([top / H, bottom / H]);
      }
      return bands;
    }

    build(source) {
      const bands = this.detectBands(source);
      if (!bands) return;

      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const displayWidth = this.img.getBoundingClientRect().width || 640;
      const scale = (displayWidth * dpr) / source.naturalWidth;

      const stack = document.createElement('div');
      stack.className = 'ny-soda-letter__reveal-stack';

      for (const [t, b] of bands) {
        const sy = Math.round(t * source.naturalHeight);
        const sh = Math.max(1, Math.round((b - t) * source.naturalHeight));
        const canvas = document.createElement('canvas');
        canvas.className = 'ny-soda-letter__band';
        canvas.width = Math.max(1, Math.round(source.naturalWidth * scale));
        canvas.height = Math.max(1, Math.round(sh * scale));
        canvas.getContext('2d').drawImage(
          source, 0, sy, source.naturalWidth, sh,
          0, 0, canvas.width, canvas.height
        );
        stack.appendChild(canvas);
      }

      this.img.after(stack);
      this.classList.add('is-analyzed'); // CSSが<img>を隠し、帯を表示する

      // 画面に入ったら上の段落から順に現す
      const io = new IntersectionObserver(
        (entries) => {
          if (!entries[0].isIntersecting) return;
          io.disconnect();
          const bandsEls = stack.children;
          for (let i = 0; i < bandsEls.length; i += 1) {
            bandsEls[i].style.transitionDelay = `${(i * 0.6).toFixed(2)}s`;
          }
          requestAnimationFrame(() => stack.classList.add('is-writing'));
        },
        { threshold: 0.12 }
      );
      io.observe(this);
    }
  }

  customElements.define('ny-soda-letter-reveal', NySodaLetterReveal);
}
